import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { LayerListBloc } from '@connect-services/client/blocs/layer-list.bloc';
import {
    AddLayerModalComponent,
    AddLayerResult,
} from '@connect-services/client/components/modals/add-layer-modal/add-layer-modal.component';
import { DepositLayersTableComponent } from '@connect-services/client/components/project-settings/project-layers-settings/deposit-layers-table/deposit-layers-table.component';
import { FluxLayersTableComponent } from '@connect-services/client/components/project-settings/project-layers-settings/flux-layers-table/flux-layers-table.component';
import { InterstandardLayersTableComponent } from '@connect-services/client/components/project-settings/project-layers-settings/interstandard-layers-table/interstandard-layers-table.component';
import { SectionHeaderComponent } from '@connect-services/client/components/section-header/section-header.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { DepositWithFolderAndStandard, Layer, PermissionAliasEnum } from '@connect-services/client/sdk/api';
import { HomeService } from '@connect-services/client/services/home.service';
import { LayerService } from '@connect-services/client/services/layer.service';
import { MapSourceService } from '@connect-services/client/services/map-source.service';
import { ProjectSettingsPermissionsService } from '@connect-services/client/services/project-settings-permissions.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { ExpansionPanelComponent } from '@connect-services/src/lib/components/expansion-panel/expansion-panel.component';
import { RessourcesModule } from '@ressources/client/ressources.module';

export type FolderAndStandardByDepositId = Map<string, DepositWithFolderAndStandard>;

export enum PanelCategoriesEnum {
    DEPOSIT = 'deposit',
    FLUX = 'flux',
    INTERSTANDARD = 'interstandard',
    REFERENCE_DATA = 'referenceData',
    TABULAR_DEPOSIT = 'tabularDeposits',
    TABULAR_INTERSTANDARD = 'tabularInterstandards',
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        DepositLayersTableComponent,
        ExpansionPanelComponent,
        FluxLayersTableComponent,
        InterstandardLayersTableComponent,
        RessourcesModule,
        SectionHeaderComponent,
    ],
    selector: 'app-project-layers-settings',
    templateUrl: './project-layers-settings.component.html',
})
export class ProjectLayersSettingsComponent {
    public hasAddLayerPermissions = false;

    protected readonly ButtonType = ButtonType;
    protected readonly Layer = Layer;
    protected readonly layerCountByCategory = signal<Record<PanelCategoriesEnum, number>>({
        [PanelCategoriesEnum.DEPOSIT]: 0,
        [PanelCategoriesEnum.TABULAR_DEPOSIT]: 0,
        [PanelCategoriesEnum.INTERSTANDARD]: 0,
        [PanelCategoriesEnum.TABULAR_INTERSTANDARD]: 0,
        [PanelCategoriesEnum.REFERENCE_DATA]: 0,
        [PanelCategoriesEnum.FLUX]: 0,
    });
    protected readonly layerListBloc = inject(LayerListBloc);
    protected readonly PanelCategoriesEnum = PanelCategoriesEnum;
    protected readonly PermissionAliasEnum = PermissionAliasEnum;
    protected readonly searchValue = signal('');
    protected readonly totalAllLayers = computed(() => {
        return Object.values(this.layerCountByCategory()).reduce((total, count) => total + count, 0);
    });

    private readonly homeService = inject(HomeService);
    private readonly layerService = inject(LayerService);
    private readonly mapSourceService = inject(MapSourceService);
    private readonly matDialog = inject(MatDialog);
    private readonly projectSettingsPermissionsService = inject(ProjectSettingsPermissionsService);

    public constructor() {
        this.hasAddLayerPermissions = this.projectSettingsPermissionsService.canManageProjectLayer();
    }

    public openAddLayerModal(): void {
        const dialogRef = this.matDialog.open(AddLayerModalComponent, {
            autoFocus: false,
            maxHeight: '50vh',
            maxWidth: '60vw',
            minHeight: '15vh',
            minWidth: '28vw',
        });

        dialogRef.afterClosed().subscribe(async (addLayerResult: AddLayerResult) => {
            if (addLayerResult.modalResult !== ModalResultEnum.CONFIRM) {
                return;
            }

            //The value didn't matter here, it's safer to do this, this way because we don't need to re-assign the value
            this.layerService.forceLayerListRefresh.set(this.layerService.forceLayerListRefresh() + 1);
            this.mapSourceService.updateMapBounds(addLayerResult.layers);

            await this.homeService.initSources();
        });
    }

    protected updateLayerCountByCategory(total: number, category: PanelCategoriesEnum): void {
        this.layerCountByCategory.update((layerCountByCategory: Record<PanelCategoriesEnum, number>) => {
            return {
                ...layerCountByCategory,
                [category]: total,
            };
        });
    }

    protected updateSearchValue(search: string): void {
        this.searchValue.set(search);
    }
}
